import React from 'react';
import type { ReactElement } from 'react';
import { spacingHelper } from '@construct-kit/core';
import styled from 'styled-components';
import { CarSpacesFilter } from '../../routes/searchResult/refinements/components/CarSpacesFilter';
import type { SelectedFiltersOptionsType } from '../model/constants/filterTypes';
import { FloorAreaFilter } from '../../routes/searchResult/refinements/components/FloorAreaFilter';
import { KeywordsFilter } from '../../routes/searchResult/refinements/components/KeywordsFilter';
import { LandAreaFilter } from '../../routes/searchResult/refinements/components/LandAreaFilter';
import { DEFAULT_FILTERS_REFINEMENTS_OPTIONS } from '../../routes/searchResult/refinements/components/mapRefinementsFilters';
import { NabersEnergyRatingFilter } from '../../routes/searchResult/refinements/components/NabersEnergyRatingFilter';
import { PriceFilter } from '../../routes/searchResult/refinements/components/PriceFilter';
import { PropertyTypesFilter } from '../../routes/searchResult/refinements/components/PropertyTypesFilter';
import { TenureTypesFilter } from '../../routes/searchResult/refinements/components/TenureTypesFilter';
import {
  tenureTypesOptions,
  carSpacesFilterOptions,
  nabersEnergyRatingFilterOptions,
} from '../../routes/searchResult/refinements/constants/filtersOptions';
import { Leased, ForLease, Invest, channelsArray } from '../model/constants/channels';
import { OTHER, propertyTypesForSelectElement } from '../model/constants/propertyTypes';
import type { AllChannelObjects } from '../model/tsTypes';
import { ChannelSwitcher } from './ChannelSwitcher';
import { customerChannelsArray } from '../model/constants/customerChannels';

type AllFiltersProps = {
  initialFilterOptions: SelectedFiltersOptionsType;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
};

const StyledFilterContainer = styled.div`
  padding: ${spacingHelper('0 0 extraLarge 0 ')};
`;

export const AllFilters = ({
  initialFilterOptions,
  selectedFiltersOptions,
  setSelectedFiltersOptions,
}: AllFiltersProps): ReactElement => {
  const {
    channel,
    tenureType,
    propertyTypes,
    searchLocations,
    floorAreaRange,
    landAreaRange,
    numParkingSpaces,
    energyEfficiency,
    priceRange,
    keywords,
  } = selectedFiltersOptions;

  const channelId = channel.id;
  const isLeased = channelId === Leased.id;
  const isForLease = channelId === ForLease.id;
  const isInvest = channelId === Invest.id;
  const isCustomerHomePage = customerChannelsArray.includes(channel);
  const channels = isCustomerHomePage ? customerChannelsArray : channelsArray;
  const propertyTypesOptions = isCustomerHomePage
    ? propertyTypesForSelectElement.filter((type) => type.value !== OTHER.id)
    : propertyTypesForSelectElement;

  const handleChannelSwitch = (changedChannel: AllChannelObjects): void => {
    if (changedChannel === initialFilterOptions.channel) {
      setSelectedFiltersOptions(initialFilterOptions);
    } else {
      setSelectedFiltersOptions({
        channel: changedChannel,
        searchLocations,
        ...DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
      });
    }
  };

  const handleFilterChange =
    <K extends keyof SelectedFiltersOptionsType>(filterName: K) =>
      (newValue: SelectedFiltersOptionsType[K]): void => {
        setSelectedFiltersOptions({
          ...selectedFiltersOptions,
          [filterName]: newValue,
        });
      };

  return (
    <>
      {!isInvest && (
        <ChannelSwitcher
          channels={channels}
          setSelectedChannel={handleChannelSwitch}
          defaultChannel={initialFilterOptions.channel}
        />
      )}
      <StyledFilterContainer>
        <PropertyTypesFilter
          options={propertyTypesOptions}
          values={propertyTypes}
          onChange={handleFilterChange('propertyTypes')}
        />
        {!isLeased && !isForLease && !isInvest && !isCustomerHomePage && (
          <TenureTypesFilter
            options={tenureTypesOptions}
            value={tenureType}
            onChange={handleFilterChange('tenureType')}
          />
        )}
        {!isLeased && (
          <PriceFilter
            selectedChannel={channel}
            searchQueryPriceRange={priceRange}
            onChange={handleFilterChange('priceRange')}
            showDivider={!isCustomerHomePage}
          />
        )}
        {!isCustomerHomePage && (
          <>
            <FloorAreaFilter
              searchQueryFloorAreaRange={floorAreaRange}
              onChange={handleFilterChange('floorAreaRange')}
            />
            <LandAreaFilter
              searchQueryLandAreaRange={landAreaRange}
              onChange={handleFilterChange('landAreaRange')}
            />
            <CarSpacesFilter
              options={carSpacesFilterOptions}
              value={numParkingSpaces}
              onChange={handleFilterChange('numParkingSpaces')}
            />
            {!isInvest && (
              <NabersEnergyRatingFilter
                options={nabersEnergyRatingFilterOptions}
                value={energyEfficiency}
                onChange={handleFilterChange('energyEfficiency')}
              />
            )}
            <KeywordsFilter onChange={handleFilterChange('keywords')} value={keywords} />
          </>
        )}
      </StyledFilterContainer>
    </>
  );
};

export default AllFilters;
